/**
 * THE ACTION TABLE — every modal the support assistant is allowed to OPEN.
 *
 * WHAT AN ACTION IS, AND WHAT IT IS NOT
 * ------------------------------------
 * An action chip does exactly two things: it records an intent on `services/supportActionBus`
 * and it navigates to the screen that hosts the modal. The screen then opens that modal,
 * pre-filled, and stops. **NOTHING HERE SUBMITS ANYTHING.** The user performs the final
 * commit themselves, on the modal, with their own click — that is the whole product rule, and
 * it is why this table names *modal openers* rather than endpoints.
 *
 * ⚠️ `method` IS A LITERAL UNION, NEVER `keyof IJobAction`.
 * The narrow type is the enforcement. `keyof IJobAction` would make adding `printLabel` to this
 * table a one-line DATA edit that compiles; as written it is a TYPE ERROR that has to be argued
 * for in a diff. The list below is the argument that has already been had — read it before
 * widening the union. It stays a union even now that five of the seven methods are in it: the
 * point was never the size of the gap, it is that a change of this kind has to be legible as a
 * change of this kind.
 *
 * ⚠️ THIS FILE IS HALF OF A CROSS-REPO CONTRACT.
 * The Function App holds the other half in `Support/answer-actions.json` and holds only KEYS.
 * `supportActions.test.ts` restates the FA's key list and fails if the two drift, the same way
 * `routes.ts` is pinned for screens. See `AnswerActionGuard.cs`.
 */

/**
 * The five action keys. A closed union, not a string: an unknown key from the wire resolves to
 * `null` through `supportActionFor` and renders no chip at all.
 */
export type SupportActionKey =
  | "start-job"
  | "stop-job"
  | "complete-resource"
  | "rework-job"
  | "complete-production-order";

export interface SupportActionDescriptor {
  /** Stable key. This is what crosses the wire. */
  readonly key: SupportActionKey;

  /**
   * The `IJobAction` method the host screen calls.
   *
   * ⚠️ A LITERAL UNION ON PURPOSE — see the header. Widening this to `keyof IJobAction` turns
   * every exclusion below into a data change nobody has to defend.
   *
   * Every method named here is an OPENER: each one ends in `setShow*Modal(true)` and posts
   * nothing. That is a property of the methods, checked by reading them, and the union is what
   * keeps it checkable — five names a reviewer can go and read, not a whole interface.
   */
  readonly method:
    | "startJob"
    | "stopJob"
    | "completeJob"
    | "reworkJob"
    | "completeProductionOrder";

  /**
   * Which screen mounts the `JobAction` instance that owns the method.
   *
   * Only `"job"` today, and that is not an accident: `pages/JobPage` is the one host that is
   * route-addressable (`job/:jobNumber`), mode-independent, already resolves the job card, and
   * already computes the real guards. `components/Header/Header.tsx` holds a fourth
   * `IJobAction` ref and is dead code; `CapacityManagerPage` mounts no `JobAction` at all.
   */
  readonly hostScreen: "job";

  /** The chip's label. Written as an OFFER to open something, never as the act itself. */
  readonly label: string;

  /**
   * What the dialog will ASK, when that is not obvious from the label.
   *
   * The panel puts this in the thread when the chip is pressed, the way it already announces a
   * Manager-view flip — a `Notice`, where the user is already reading, not a tooltip nobody
   * opens. Only `start-job` carries one today and the reason is specific: on an operator who is
   * already clocked onto another production order, that dialog's first question is whether to
   * STOP that other one. The user still answers it, so the never-submit rule holds — but being
   * handed a question about a job the conversation never mentioned is how a rule that is
   * technically kept still surprises somebody. Say it before, not after.
   */
  readonly caution?: string;
}

/**
 * WHAT IS DELIBERATELY NOT HERE, AND WHY — AND WHAT USED TO BE.
 *
 * `IJobAction` exposes seven methods. Five are offered and two are refused. This list is the
 * record of those decisions; if a refusal is ever lifted, the reason has to be shown to be no
 * longer true, and the literal union above is what forces that conversation to happen.
 *
 * • `addJobNote` — REMOVED 2026-09-14, BY PRODUCT DECISION RATHER THAN BY RISK. It was the
 *   safest thing on the list and it earned its place least: nobody is stuck finding the Notes
 *   button, and every key costs a line of the model's prompt and a chip competing for a ~350px
 *   panel. Nothing about it became unsafe — so if the argument for it comes back, this is a
 *   one-line reinstatement and not a re-litigation.
 *
 * • `printLabel` — safe in every code sense, and still excluded: printing is an UNCANCELLABLE
 *   PHYSICAL SIDE EFFECT. Paper comes out of a machine in a building, and there is no undo
 *   button for that. A help feature must not be able to cause one. This is the only refusal on
 *   the list that is about the world rather than about the code, and it is the one least likely
 *   to be revisited.
 *
 * ⚠️ AND WHAT THE OTHER FOUR WERE REFUSED FOR, SINCE THE REASONS ARE STILL TRUE — THEY WERE
 * ANSWERED, NOT WITHDRAWN. Each was excluded for something real; what changed is where the
 * answer lives, which is `pages/JobPage` (the guards) and this file (the honesty about what a
 * dialog will ask).
 *
 * • `startJob` — CHAINED, AND THE CHAIN TOUCHES A DIFFERENT JOB. When the user already has a
 *   job started it opens a confirmation whose OK button STOPS THAT OTHER JOB before starting
 *   this one (`JobAction.tsx:202-216`). The never-submit rule survives — that OK is the user's
 *   own press, on a dialog that names the other job — but a rule kept on a technicality is not
 *   good enough, so the chip carries a `caution` the panel states in the thread BEFORE the
 *   dialog appears. Gated on `canStart`.
 *
 * • `stopJob` — ROUTES TO A DIFFERENT MODAL BY A HEURISTIC, AND SOMETIMES TO NONE. It picks
 *   `DeleteJobModal` over `StopJobModal` on a `<30s` elapsed test (`JobAction.tsx:302`) —
 *   which is the correct behaviour and the same one the ribbon's Stop button gets, so the chip
 *   is not entitled to a different one. Its two open-nothing branches are refusals, not dead
 *   clicks: each already toasts a specific sentence ("No started job", or the name of the job
 *   they ARE clocked onto). Do not make this chip quieter than the button beside it. Gated on
 *   `canStop`.
 *
 * • `completeJob` (the `complete-resource` chip) — reads two endpoints before anything is
 *   shown, which was the objection. It is a READ of the server's own answer to "who is clocked
 *   onto this, and may a resource be completed out of sequence" — deliberately re-read on every
 *   open because the board's card goes stale — and the dialog then waits. Gated on `canComplete`.
 *
 * • `completeProductionOrder` — the heaviest of them, and the one with the sharpest rule
 *   attached. The workflow behind its OK has a cross-user lock, outbound e-mail and a B1
 *   write-back; none of that is reached by opening the dialog. ⚠️ THE CHIP PASSES NO
 *   `resourceTime` AND MUST NOT: that argument seeds the completion quantity through
 *   `getCompletePOQuantity` (`JobAction.tsx:162-172`), whose helper has a recorded
 *   negative-value bug, and the chat has no access to the resources-table snapshot the ribbon
 *   button takes. Opening it on the right order and letting the screen fill itself in is the
 *   whole of what this chip is for. Gated on `canCompletePO`.
 */
const ACTIONS: readonly SupportActionDescriptor[] = Object.freeze([
  {
    key: "start-job",
    method: "startJob",
    hostScreen: "job",
    label: "Open the start form",
    caution:
      "If you're already clocked onto another production order, the start form will ask " +
      "whether to stop that one first. Nothing is stopped or started until you answer it.",
  },
  {
    key: "stop-job",
    method: "stopJob",
    hostScreen: "job",
    label: "Open the stop form",
  },
  {
    key: "complete-resource",
    method: "completeJob",
    hostScreen: "job",
    label: "Open the complete resource form",
  },
  {
    key: "rework-job",
    method: "reworkJob",
    hostScreen: "job",
    label: "Open the rework form",
  },
  {
    key: "complete-production-order",
    method: "completeProductionOrder",
    hostScreen: "job",
    label: "Open the complete production order form",
  },
]);

/** Every action key, for the contract test and for exhaustiveness checks. */
export const SUPPORT_ACTION_KEYS: readonly SupportActionKey[] = Object.freeze(
  ACTIONS.map((a) => a.key)
);

const BY_KEY: ReadonlyMap<string, SupportActionDescriptor> = new Map(
  ACTIONS.map((a) => [a.key, a])
);

/**
 * Look an action up by key. An unknown key — an older or newer Function App, or a model that
 * invented one — returns `null`. It never throws and never guesses: a chip that cannot be
 * resolved is a chip that is not rendered.
 */
export const supportActionFor = (
  key: string | null | undefined
): SupportActionDescriptor | null => (key && BY_KEY.get(key)) || null;
